/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { Box, CircularProgress } from '@mui/material';

import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { removeNotification } from '../../store/notifications/actions';
import { INotification } from '../../store/notifications/types';
import { AppServerEvents } from '../../types/common/AppEvents';
import { NotificationItemFrameOptions } from '../../types/notification/NotificationItemFrameOptions';
import { determineStatusColor } from '../../utils/determineStatusColor';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { fetchCluster } from '../../utils/queryFns';
import { useNotificationRelativeDate } from '../../utils/useNotificationRelativeDate';
import { AlignCenter } from '../AlignCenter/AlignCenter';
import { NotificationItemFrame } from '../NotificationItemFrame/NotificationItemFrame';

type ClusterStatusNotificationProps = NotificationItemFrameOptions & {
  notification: INotification<
    AppServerEvents.ClusterCreated | AppServerEvents.ClusterUpdated | AppServerEvents.ClusterDeleted
  >;
};

export function ClusterStatusNotification(props: ClusterStatusNotificationProps) {
  const { notification, setNotificationAsRead, asSnackbar, onRemoveBtnClick, onRootClick } = props;
  const { event, createdAt, isRead } = notification;
  const { status, clusterId, organizationId, projectId } = event;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation([Namespaces.Navigation, Namespaces.Common]);

  const clusterQuery = useQuery(
    [
      fetchCluster.key,
      {
        organizationId,
        clusterId,
        projectId,
      },
    ],
    fetchCluster.fn,
    { refetchOnWindowFocus: false, retry: 1 },
  );

  const clusterData = useMemo(() => clusterQuery.data?.payload, [clusterQuery.data]);

  const [datetime] = useNotificationRelativeDate(createdAt);

  useEffect(() => {
    if (clusterQuery.status === 'error') {
      dispatch(removeNotification({ id: notification.id }));
    }
  }, [clusterQuery.status, dispatch, notification.id]);

  const onClick = () => {
    onRootClick && onRootClick({ id: notification.id });
    void navigate(
      prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
        ':organization_id': organizationId,
        ':project_id': projectId,
        ':cluster_id': clusterId,
      }),
    );
  };
  return clusterData ? (
    <NotificationItemFrame
      setNotificationAsRead={setNotificationAsRead}
      datetime={datetime}
      isRead={asSnackbar ? true : isRead}
      id={notification.id}
      onRemoveBtnClick={onRemoveBtnClick}
      asSnackbar={asSnackbar}
      onRootClick={onClick}
      primaryText={
        <>
          {t('notifications.labels.status_dd')} &nbsp;
          <span style={{ color: determineStatusColor(status as any) }}>
            {t(`${Namespaces.Entities}:cluster.k8s_status.values.${status.toLowerCase()}`)}
          </span>
        </>
      }
      secondaryText={clusterData.name}
    />
  ) : (
    <AlignCenter>
      <Box sx={{ padding: '5px' }}>
        <CircularProgress color="primary" size={20} />
      </Box>
    </AlignCenter>
  );
}
